import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_FORM_SETTINGS } from '../../graphql';
import { UPDATE_TINYCMS_FORM_SETTINGS } from '../../graphql/mutations';
import { InkSection } from './shared';
import { Loader } from '../common/PageLoader';

/**
 * Content → Forms → Form settings ("tinycms UX optimization review v7",
 * Prototype v3): how long responses are kept and what readers see under every
 * form. Responses stay in Forms — nothing is emailed. Account admins change
 * these; everyone else sees them.
 */

export const RETENTION_CHOICES: Array<{ months: number; label: string }> = [
  { months: 1, label: '1 month' },
  { months: 3, label: '3 months' },
  { months: 6, label: '6 months' },
  { months: 12, label: '12 months' },
  { months: 24, label: '2 years' },
  { months: 36, label: '3 years' },
  { months: 0, label: 'Until I delete them' },
];

export const retentionText = (months: number) =>
  months === 0
    ? 'Kept until you delete them.'
    : `${RETENTION_CHOICES.find((c) => c.months === months)?.label || `${months} months`}, then they’re deleted.`;

export function CmsFormSettings() {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_FORM_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [save, { loading: saving }] = useMutation(UPDATE_TINYCMS_FORM_SETTINGS, {
    refetchQueries: [GET_TINYCMS_FORM_SETTINGS],
  });
  const settings = data?.tinycmsFormSettings;
  const canManage = Boolean(settings?.canManage);
  const [consent, setConsent] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (settings) setConsent(settings.consent || '');
  }, [settings]);

  const update = async (input: Record<string, unknown>, done: string) => {
    setMessage(null);
    try {
      await save({ variables: { accountId, input } });
      setMessage({ tone: 'ok', text: done });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Couldn’t save' });
    }
  };

  const changeRetention = (months: number) => {
    if (
      settings &&
      months !== 0 &&
      (settings.retentionMonths === 0 || months < settings.retentionMonths) &&
      !window.confirm(
        `Keep responses for ${RETENTION_CHOICES.find((c) => c.months === months)?.label}?\n\nResponses older than that will be deleted and can’t be brought back.`
      )
    )
      return;
    void update({ retentionMonths: months }, 'Saved.');
  };

  return (
    <div className="ink-body">
      <div className="ink-site-settings-head">
        <Link to="/content?view=forms" className="ink-back-link">
          ← Forms
        </Link>
        <h1 className="ink-h1">Form settings</h1>
        <p className="ink-lede">
          How long responses are kept and what readers see. Applies to every form your agent builds.
        </p>
      </div>
      {loading && !settings ? <Loader label="Loading" className="ink-muted" /> : null}
      {settings ? (
        <>
          <InkSection
            title="Responses"
            description="Every response is kept in Forms, for you and the people on this account. They aren’t emailed and your agent can’t read them."
          >
            <div className="ink-site-card">
              <div className="ink-site-row">
                <span>
                  <span className="ink-site-row-title">Keep responses for</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    {retentionText(settings.retentionMonths)}
                  </span>
                </span>
                <select
                  className="ink-site-input"
                  aria-label="Keep responses for"
                  style={{ width: 'auto' }}
                  value={settings.retentionMonths}
                  disabled={!canManage || saving}
                  onChange={(e) => changeRetention(Number(e.target.value))}
                >
                  {RETENTION_CHOICES.map((c) => (
                    <option key={c.months} value={c.months}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </InkSection>
          <InkSection
            title="Protection"
            description="What readers see and agree to when they send a form."
          >
            <div className="ink-site-card">
              <div className="ink-site-row">
                <span>
                  <span className="ink-site-row-title">Spam protection</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    An invisible check. Readers never solve a puzzle.
                  </span>
                </span>
                <span className="ink-media-rule">Always on</span>
              </div>
              <div className="ink-site-row" style={{ alignItems: 'flex-start' }}>
                <span style={{ flex: 1 }}>
                  <span className="ink-site-row-title">Consent line</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    Shown under every form. Leave it empty to show none.
                  </span>
                  <input
                    className="ink-site-input"
                    style={{ marginTop: 8, width: '100%' }}
                    value={consent}
                    maxLength={200}
                    disabled={!canManage}
                    onChange={(e) => setConsent(e.target.value)}
                    aria-label="Consent line"
                  />
                </span>
                <button
                  type="button"
                  className="ink-btn ink-btn-quiet"
                  style={{ marginTop: 28 }}
                  disabled={!canManage || saving || consent === (settings.consent || '')}
                  onClick={() =>
                    void update({ consent }, 'Saved. Live forms show it within a minute.')
                  }
                >
                  Save
                </button>
              </div>
            </div>
          </InkSection>
          {!canManage ? <p className="ink-muted">Only an account admin can change these.</p> : null}
          {message ? (
            <p className={message.tone === 'error' ? 'text-xs text-error' : 'ink-muted'}>
              {message.text}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
